import React, { useMemo, useState } from "react";
import {
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Box,
  Chip,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import FantasyDriverDetailsDrawer from "./FantasyDriverDetailsDrawer";
import { lastNRaces, rankGroup } from "./fantasyInsights";
import { useDriverCarNumbers } from "../hooks/useDriverCarNumbers";
import { useDriverManufacturers } from "../hooks/useDriverManufacturers";
import { useFantasyDrawer } from "./hooks/useFantasyDrawer";
import { tableContainerSx } from "../styles/tableStyles";
import { stickyOnPhone, phoneNumberColumn, PHONE_NUMBER_COLUMN } from "../styles/responsive";
import ManufacturerLogo from "./ManufacturerLogo";

const MEASURES = [
  { key: "fantasy_points", label: "Fantasy points" },
  { key: "race_pos", label: "Finish position" },
];

const headerSx = {
  fontWeight: "bold",
  textAlign: "center",
  bgcolor: (theme) => (theme.palette.mode === "dark" ? "#4F4F4F" : "grey.300"),
};

// The drivers of the selected group ordered by how they rank against each other at this track, on similar tracks
// and in the last 5 races. The group is whatever the server currently has for it, so it follows the
// recalculation after each race.
const FantasyRankingTable = ({ groupDrivers, trackRaces, similarTrackRaces, allRaces, currentSeasonRaces, pastSeasonRaces }) => {
  const [measure, setMeasure] = useState("fantasy_points");

  const { selectedDriver, handleDriverClick, closeDrawer } = useFantasyDrawer();
  const driverCarNumbers = useDriverCarNumbers(allRaces || currentSeasonRaces);
  const driverManufacturers = useDriverManufacturers(allRaces || currentSeasonRaces);

  const rows = useMemo(
    () => rankGroup(
      groupDrivers,
      { track: trackRaces, similar: similarTrackRaces, form: lastNRaces(currentSeasonRaces, 5) },
      measure
    ),
    [groupDrivers, trackRaces, similarTrackRaces, currentSeasonRaces, measure]
  );

  // an average and the driver's rank for it in the group
  const measureCell = (row, key) => (
    <TableCell align="center" sx={{ fontWeight: row.ranks[key] !== null && row.ranks[key] <= 3 ? "bold" : "normal" }}>
      {row.ranks[key] === null ? "-" : `${row.averages[key].toFixed(1)} (${row.ranks[key]})`}
    </TableCell>
  );

  return (
    <Paper sx={{ borderRadius: 3, p: 3, boxShadow: 3 }}>
      <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" alignItems="center" sx={{ mb: 2 }}>
        {MEASURES.map(({ key, label }) => (
          <Chip key={key} label={label} color={measure === key ? "primary" : "default"} variant={measure === key ? "filled" : "outlined"} onClick={() => setMeasure(key)} clickable />
        ))}
      </Stack>

      <TableContainer sx={tableContainerSx}>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: (theme) => (theme.palette.mode === "dark" ? "grey.800" : "grey.200") }}>
              <TableCell sx={[{ fontWeight: "bold", px: 2 }, phoneNumberColumn, stickyOnPhone(0, { header: true })]}>#</TableCell>
              <TableCell sx={[{ fontWeight: "bold", px: 2 }, stickyOnPhone(PHONE_NUMBER_COLUMN, { header: true })]}>Driver</TableCell>
              <Tooltip title="Mean of the ranks below. Lower is better.">
                <TableCell sx={headerSx}>Score</TableCell>
              </Tooltip>
              <Tooltip title="Average at this track, and the rank in the group (1 = best)">
                <TableCell sx={headerSx}>At track</TableCell>
              </Tooltip>
              <Tooltip title="Average on similar tracks, and the rank in the group">
                <TableCell sx={headerSx}>Similar tracks</TableCell>
              </Tooltip>
              <Tooltip title="Average over the last 5 races of the season, and the rank in the group">
                <TableCell sx={headerSx}>Last 5</TableCell>
              </Tooltip>
            </TableRow>
          </TableHead>

          <TableBody>
            {rows.map((row, index) => (
              <TableRow
                key={row.driver}
                sx={{
                  bgcolor: index % 2 === 0 ? "background.default" : "action.hover",
                  "&:hover": { bgcolor: "action.selected", transition: "all 0.3s ease" },
                }}
              >
                <TableCell sx={[{ width: "40px", fontWeight: "bold", px: 2 }, phoneNumberColumn, stickyOnPhone(0, { odd: index % 2 === 1 })]}>{driverCarNumbers[row.driver] || "-"}</TableCell>
                <TableCell sx={[{ fontWeight: "bold", px: 2, cursor: "pointer" }, stickyOnPhone(PHONE_NUMBER_COLUMN, { odd: index % 2 === 1 })]} onClick={() => handleDriverClick(row.driver)}>
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <ManufacturerLogo manufacturer={driverManufacturers[row.driver]} />
                    {row.driver}
                  </Box>
                </TableCell>
                <TableCell align="center" sx={{ fontWeight: "bold" }}>{row.score === null ? "-" : row.score.toFixed(1)}</TableCell>
                {measureCell(row, "track")}
                {measureCell(row, "similar")}
                {measureCell(row, "form")}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 2 }}>
        Each driver is ranked within the selected group on each measure (1 = best); the score is the mean of the ranks
        the driver has, so a driver with no race at this track is judged on the others. Ranks 1 to 3 are in bold.
      </Typography>

      {selectedDriver && (
        <FantasyDriverDetailsDrawer
          driver={selectedDriver}
          groupDrivers={groupDrivers}
          trackRaces={trackRaces}
          similarTrackRaces={similarTrackRaces}
          allRaces={allRaces}
          pastSeasonRaces={pastSeasonRaces}
          currentSeasonRaces={currentSeasonRaces}
          onClose={closeDrawer}
        />
      )}
    </Paper>
  );
};

export default FantasyRankingTable;
